import React from 'react';
import { Shield } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useSeason } from '../../context/SeasonContext';

export default function ContractConfidenceSection() {
  const { t } = useLanguage();
  const { theme } = useSeason();

  const items = [
    {
      title: t('home.contract.item1_title', 'Know what is confirmed'),
      description: t(
        'home.contract.item1_body',
        'Separate owner-confirmed details from estimates and open questions before you treat a listing as ready to sign.'
      ),
    },
    {
      title: t('home.contract.item2_title', 'Ask better lease questions'),
      description: t(
        'home.contract.item2_body',
        'Use plain-language prompts around deposits, utilities, notice periods, handover timing, and what still needs clarification.'
      ),
    },
    {
      title: t('home.contract.item3_title', 'Slow down the scary moment'),
      description: t(
        'home.contract.item3_body',
        'Contract confidence is about clarity before commitment. TLA Housing Europe helps you prepare, not replace professional legal advice.'
      ),
    },
  ];

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-amber-100"
      data-testid="contract-confidence-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 border"
              style={{
                backgroundColor: `${theme.colors.primary}12`,
                borderColor: `${theme.colors.primary}35`,
              }}
            >
              <Shield className="w-4 h-4" style={{ color: theme.colors.primary }} />
              <span style={{ color: theme.colors.primary }} className="text-sm font-medium">
                {t('home.contract.badge', 'Contract Confidence')}
              </span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              {t('home.contract.heading', 'TAKE A BREATH BEFORE YOU SIGN')}
            </h2>

            <p className="text-slate-600 text-lg mb-5">
              {t(
                'home.contract.subtitle',
                'Relocation contracts can feel intimidating when the language, deposits, utilities, and timing are unfamiliar. TLA Housing Europe helps you slow the moment down and understand what to ask before you commit.'
              )}
            </p>

            <p className="text-sm text-slate-500 bg-amber-50 border border-amber-100 rounded-xl p-4">
              {t(
                'home.contract.disclaimer',
                'TLA Housing Europe provides practical relocation guidance and plain-language preparation prompts. It does not replace legal, financial, or professional advice.'
              )}
            </p>
          </div>

          <div className="grid gap-4">
            {items.map((item) => (
              <div key={item.title} className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
